import { useState, useEffect, useRef } from "react"
import { Box } from "@mui/material"
import { newsletterImagesApi } from "@crm-src/lib/newsletter-images-api"
import { useToast } from "@crm-src/hooks/use-toast"
import { SpeakerPhotoCropModal } from "@crm-src/components/speaker-photo-crop-modal"
import {
  Speaker,
  TenantInfo,
  WebinarSection,
  headerImageOptions,
} from "@crm-src/components/webinar-builder/stock-images"
import {
  HeaderSection,
  headerToHtml,
} from "@crm-src/components/webinar-builder/header-section"
import {
  WebinarDetailsSection,
  webinarDetailsToHtml,
} from "@crm-src/components/webinar-builder/webinar-details-section"
import {
  SpeakersSection,
  speakersToHtml,
} from "@crm-src/components/webinar-builder/speakers-section"
import {
  FooterSection,
  footerToHtml,
} from "@crm-src/components/webinar-builder/footer-section"
import {
  EMAIL_DOCUMENT_OPEN,
  EMAIL_DOCUMENT_CLOSE,
} from "@crm-src/components/webinar-builder/email-template"

interface WebinarBuilderProps {
  tenantName: string
  tenantLogo?: string | null
  tenantInfo?: TenantInfo
  initialSections?: WebinarSection[]
  onContentChange: (html: string) => void
  onSectionsChange?: (sections: WebinarSection[]) => void
}

export function WebinarBuilder({
  tenantName,
  tenantLogo,
  tenantInfo,
  initialSections,
  onContentChange,
  onSectionsChange,
}: WebinarBuilderProps) {
  // Default the date input to one month out so brand-new webinars start with a
  // sensible placeholder rather than today/yesterday.
  const currentDate = new Date()
  const nextMonth = new Date(currentDate.setMonth(currentDate.getMonth() + 1))
  const defaultDate = nextMonth.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

  const getDefaultSections = (): WebinarSection[] => [
    {
      id: 'header',
      type: 'header',
      content: {
        title: 'You\'re Invited!',
        subtitle: 'Webinar Invitation!',
        overlayText: '',
        imageUrl: headerImageOptions[0].url,
      },
    },
    {
      id: 'webinar-details',
      type: 'webinar_details',
      backgroundColor: '#ffffff',
      content: {
        title: 'Maximizing CRM ROI: Best Practices and Strategies',
        tagline: 'Join us for an insightful session',
        date: defaultDate,
        time: '2:00 PM EST',
        duration: '60 minutes',
        location: '',
        registrationLink: '',
        description: '<p>Join us for an insightful webinar where we will explore proven strategies to maximize your CRM investment and drive business growth. We will cover best practices, real-world examples, and actionable tips you can implement right away.</p><p>This session is perfect for business owners, sales managers, and CRM administrators looking to get more value from their systems.</p>',
      },
    },
    {
      id: 'speakers',
      type: 'speakers',
      backgroundColor: '#f7fafc',
      content: {
        enabled: true,
        heading: 'Meet the Speakers',
        speakers: [
          { id: 'speaker-1', name: 'Speaker Name', title: 'Job Title\nCompany / Role', photoUrl: '', objectPosition: '50% 50%' },
          { id: 'speaker-2', name: 'Speaker Name', title: 'Job Title\nCompany / Role', photoUrl: '', objectPosition: '50% 50%' },
          { id: 'speaker-3', name: 'Speaker Name', title: 'Job Title\nCompany / Role', photoUrl: '', objectPosition: '50% 50%' },
        ] as Speaker[],
      },
    },
    {
      id: 'footer',
      type: 'footer',
      backgroundColor: '#2d3748',
      content: {},
    },
  ]

  // Ensure all required sections exist, merging saved sections with defaults.
  // Speakers is a newer optional section: when restoring an old campaign that
  // never had it, default to disabled so the rendered email doesn't change.
  const ensureAllSections = (savedSections: WebinarSection[] | undefined): WebinarSection[] => {
    if (!savedSections || savedSections.length === 0) {
      return getDefaultSections()
    }

    const defaults = getDefaultSections()
    const result: WebinarSection[] = []

    defaults.forEach(defaultSection => {
      const savedSection = savedSections.find(s => s.type === defaultSection.type)
      if (savedSection) {
        result.push(savedSection)
      } else {
        if (defaultSection.type === 'speakers') {
          result.push({
            ...defaultSection,
            content: { ...defaultSection.content, enabled: false },
          })
        } else {
          console.warn(`Missing ${defaultSection.type} section in saved data, using default`)
          result.push(defaultSection)
        }
      }
    })

    return result
  }

  const [sections, setSections] = useState<WebinarSection[]>(() => ensureAllSections(initialSections))
  const [isUploadingHeaderImage, setIsUploadingHeaderImage] = useState(false)
  const [customHeaderImages, setCustomHeaderImages] = useState<Array<{ id: number; url: string; name: string }>>([])
  const [uploadingSpeakerId, setUploadingSpeakerId] = useState<string | null>(null)
  const [cropTarget, setCropTarget] = useState<{ sectionId: string; speakerId: string } | null>(null)
  const headerImageInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  // Load custom uploaded images on mount so the user's previously-uploaded
  // header images appear next to the built-in stock options in the picker.
  useEffect(() => {
    const loadCustomImages = async () => {
      try {
        const images = await newsletterImagesApi.list()
        const formattedImages = images.map((img, index) => ({
          id: 1000 + index,
          url: img.url,
          name: img.name || 'Custom Image',
        }))
        setCustomHeaderImages(formattedImages)
      } catch (error) {
        console.error('Failed to load custom images:', error)
      }
    }
    loadCustomImages()
  }, [])

  // Re-hydrate sections when the parent supplies a fresh `initialSections`
  // payload (e.g. after switching between campaigns).
  useEffect(() => {
    if (initialSections && initialSections.length > 0) {
      const loadedSections = ensureAllSections(initialSections)
      const webinarSection = loadedSections.find(s => s.type === 'webinar_details')
      if (webinarSection) {
      }
      setSections(loadedSections)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSections])

  // Track last serialized sections + last emitted HTML to short-circuit the
  // effect — without this, the parent's onContentChange identity changes can
  // produce infinite render cycles.
  const lastSectionsJsonRef = useRef<string>("")
  const lastContentHtmlRef = useRef<string>("")

  const updateSection = (sectionId: string, newContent: any) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, content: { ...section.content, ...newContent } }
        : section
    ))
  }

  const changeSectionBgColor = (sectionId: string, color: string) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, backgroundColor: color }
        : section
    ))
  }

  const updateSpeaker = (sectionId: string, speakerId: string, patch: Partial<Speaker>) => {
    setSections(prev => prev.map(section => {
      if (section.id !== sectionId) return section
      const speakers: Speaker[] = Array.isArray(section.content.speakers) ? section.content.speakers : []
      return {
        ...section,
        content: {
          ...section.content,
          speakers: speakers.map(s => s.id === speakerId ? { ...s, ...patch } : s),
        },
      }
    }))
  }

  // Lookup helper used by the crop modal — derives the live speaker record
  // from `cropTarget` so the modal always edits the current name/photoUrl.
  const findSpeaker = (sectionId: string, speakerId: string): Speaker | undefined => {
    const section = sections.find(s => s.id === sectionId)
    if (!section) return undefined
    const speakers: Speaker[] = Array.isArray(section.content.speakers) ? section.content.speakers : []
    return speakers.find(s => s.id === speakerId)
  }

  // Compose the complete email HTML by stitching together the per-section
  // builders. Speakers is a no-op as a standalone email section and instead
  // its HTML is spliced into webinar_details, between the event cards and
  // the About description, by passing it through `webinarDetailsToHtml`.
  const generateHtmlFromSections = (sectionsToRender: WebinarSection[]): string => {
    const speakersSection = sectionsToRender.find(s => s.type === 'speakers')
    const speakersHtml = speakersToHtml(speakersSection)

    let html = EMAIL_DOCUMENT_OPEN

    sectionsToRender.forEach(section => {
      switch (section.type) {
        case 'header':
          html += headerToHtml(section, tenantName, tenantLogo)
          break
        case 'webinar_details':
          html += webinarDetailsToHtml(section, speakersHtml)
          break
        case 'speakers':
          // Speakers are rendered inline inside webinar_details (between the
          // date/time/duration cards and the About description). The standalone
          // section is intentionally a no-op in the email HTML.
          break
        case 'footer':
          html += footerToHtml(section, tenantName, tenantInfo, new Date().getFullYear())
          break
      }
    })

    html += EMAIL_DOCUMENT_CLOSE

    return html
  }

  // Generate HTML from sections whenever they change. Guarded by JSON / HTML
  // comparison refs to avoid infinite render loops via the parent callbacks.
  useEffect(() => {
    const sectionsJson = JSON.stringify(sections)
    if (sectionsJson === lastSectionsJsonRef.current) {
      return
    }
    lastSectionsJsonRef.current = sectionsJson

    const html = generateHtmlFromSections(sections)

    const webinarSection = sections.find(s => s.type === 'webinar_details')
    if (webinarSection) {
    }

    if (html !== lastContentHtmlRef.current) {
      lastContentHtmlRef.current = html
      onContentChange(html)
    }

    if (onSectionsChange) {
      onSectionsChange(sections)
    }
  }, [sections, onContentChange, onSectionsChange])

  // ---------- File-upload handlers (kept here because they call the toast +
  // newsletterImagesApi singletons and dispatch back into section state) ----------

  const handleHeaderImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setIsUploadingHeaderImage(true)
      const uploadedImage = await newsletterImagesApi.upload(file)
      updateSection('header', { imageUrl: uploadedImage.url })
      // Show the new image in the custom-images strip alongside the stock options.
      setCustomHeaderImages(prev => [...prev, {
        id: 1000 + prev.length,
        url: uploadedImage.url,
        name: file.name,
      }])
      toast({
        title: "Success",
        description: "Header image uploaded successfully",
      })
    } catch (error) {
      console.error('Failed to upload image:', error)
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      })
    } finally {
      setIsUploadingHeaderImage(false)
      if (headerImageInputRef.current) {
        headerImageInputRef.current.value = ''
      }
    }
  }

  const handleSpeakerPhotoUpload = async (
    sectionId: string,
    speakerId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setUploadingSpeakerId(speakerId)
      const uploadedImage = await newsletterImagesApi.upload(file)
      // Reset framing to centered when a new photo is uploaded so previous
      // crop offsets don't apply to a different image.
      updateSpeaker(sectionId, speakerId, { photoUrl: uploadedImage.url, objectPosition: '50% 50%' })
      toast({ title: "Success", description: "Speaker photo uploaded" })
    } catch (error) {
      console.error('Failed to upload speaker photo:', error)
      toast({
        title: "Error",
        description: "Failed to upload speaker photo",
        variant: "destructive",
      })
    } finally {
      setUploadingSpeakerId(null)
      // Reset the input so re-selecting the same file fires `change` again.
      // The ref now lives inside SpeakersSection, so resetting via the event
      // target (which IS that input element) is the simplest equivalent.
      if (event.target) {
        event.target.value = ''
      }
    }
  }

  // ---------- Section-by-section render dispatch ----------

  const renderSection = (section: WebinarSection) => {
    const bgColor = section.backgroundColor || '#ffffff'

    switch (section.type) {
      case 'header':
        return (
          <HeaderSection
            section={section}
            bgColor={bgColor}
            customHeaderImages={customHeaderImages}
            isUploadingHeaderImage={isUploadingHeaderImage}
            headerImageInputRef={headerImageInputRef}
            onUpdate={updateSection}
            onChangeBgColor={changeSectionBgColor}
            onHeaderImageUpload={handleHeaderImageUpload}
          />
        )

      case 'webinar_details':
        return (
          <WebinarDetailsSection
            section={section}
            bgColor={bgColor}
            onUpdate={updateSection}
            onChangeBgColor={changeSectionBgColor}
          />
        )

      case 'speakers':
        return (
          <SpeakersSection
            section={section}
            bgColor={bgColor}
            uploadingSpeakerId={uploadingSpeakerId}
            onUpdate={updateSection}
            onUpdateSpeaker={updateSpeaker}
            onSpeakerPhotoUpload={handleSpeakerPhotoUpload}
            onOpenCrop={(sectionId, speakerId) => setCropTarget({ sectionId, speakerId })}
          />
        )

      case 'footer':
        return (
          <FooterSection
            section={section}
            tenantName={tenantName}
            tenantInfo={tenantInfo}
            year={new Date().getFullYear()}
          />
        )

      default:
        return null
    }
  }

  const cropSpeaker = cropTarget ? findSpeaker(cropTarget.sectionId, cropTarget.speakerId) : undefined

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Visual Email Builder */}
      <Box
        sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", bgcolor: "background.paper", maxWidth: "800px", mx: "auto" }}
      >
        {sections.map((section) => (
          <div key={section.id}>
            {renderSection(section)}
          </div>
        ))}
      </Box>

      {/* Speaker photo crop modal — renders only when a speaker with a photo
          is targeted, so the modal mounts/unmounts cleanly per session. */}
      <SpeakerPhotoCropModal
        open={!!cropTarget && !!cropSpeaker?.photoUrl}
        photoUrl={cropSpeaker?.photoUrl || ''}
        initialPosition={cropSpeaker?.objectPosition || '50% 50%'}
        speakerName={cropSpeaker?.name}
        onCancel={() => setCropTarget(null)}
        onApply={(objectPosition) => {
          if (cropTarget) {
            updateSpeaker(cropTarget.sectionId, cropTarget.speakerId, { objectPosition })
          }
          setCropTarget(null)
        }}
      />
    </Box>
  )
}
